import { useState, useEffect, useCallback } from 'react';
import _ from 'lodash';
import { compareDeep } from '../object';

export const generateInput = (onChange, value) => rest => ({
  onChange,
  value,
  ...rest
});

const VALUE = 'value';
const ERROR = 'error';
const SUGGESTION = 'suggestion';
const REQUIRED_FIELD_ERROR = 'Este es un campo obligatorio.';

const shouldBeArray = ['billingEmails', 'payrollEmails', 'technicalIssuesEmails'];

const isRequiredAndEmpty = (value, maybeRequired) => !value && maybeRequired;

const reduceFeedback = (values, key, validateField) =>
  Object.keys(values).reduce((accu, curr) => {
    const processedField = validateField(curr, values[curr]);
    if (typeof processedField === 'object' && processedField.hasOwnProperty(key)) {
      accu[curr] = processedField[key];
    }
    return accu;
  }, {});

const withoutAvailableAmount = ({ availableAmount, ...rest }) => rest;

const getPropValues = (stateSchema, prop) =>
  Object.fromEntries(Object.entries(stateSchema).map(([key, value]) => [[key], value[prop]]));

const useForm = (stateSchema, validatorSchema, submitForm, defaultOptions) => {
  const [state, setStateSchema] = useState(stateSchema);

  const [values, setValues] = useState(getPropValues(state, VALUE));
  const [errors, setErrors] = useState({});
  const [suggestions, setSuggestions] = useState({});
  const [dirty, setDirty] = useState(getPropValues(state, false));

  const [disable, setDisable] = useState(true);
  const [isDirty, setIsDirty] = useState(false);

  const validateField = useCallback(
    (name, value) => {
      const fieldValidator = validatorSchema[name];
      if (!fieldValidator) {
        return;
      }

      if (isRequiredAndEmpty(value, fieldValidator?.required)) {
        setFieldError({ name, error: REQUIRED_FIELD_ERROR });
        return { error: REQUIRED_FIELD_ERROR };
      }

      if (fieldValidator?.validator?.validate(value, values)) {
        return fieldValidator?.validator?.validate(value, values);
      }
      return false;
    },
    [validatorSchema, values]
  );

  const setFieldError = ({ name, error }) => {
    setErrors(prevState => ({
      ...prevState,
      [name]: { error, ...prevState[name] }
    }));
  };

  const setFieldSuggestion = ({ name, suggestion }) => {
    setSuggestions(prevState => ({
      ...prevState,
      [name]: { suggestion, ...prevState[name] }
    }));
  };

  const setFieldIsOk = ({ name }) => {
    setErrors(prevState => ({ ...prevState, [name]: false }));
    setSuggestions(prevState => ({ ...prevState, [name]: false }));
  };

  const setInitialErrorState = useCallback(() => {
    Object.keys(errors).map(name =>
      setFieldError({
        name,
        error: validateField(name, values[name])?.error || null
      })
    );
  }, [errors, values, validateField]);

  const setInitialSuggestionsState = useCallback(() => {
    Object.keys(suggestions).map(name =>
      setFieldSuggestion({
        name,
        suggestion: validateField(name, values[name])?.suggestion || null
      })
    );
  }, [suggestions, values, validateField]);

  useEffect(() => {
    setStateSchema(stateSchema);

    setInitialErrorState();
    setInitialSuggestionsState();
  }, []); // eslint-disable-line

  useEffect(() => {
    const diff = compareDeep(values, state);
    if (diff !== {}) {
      setValues(getPropValues(stateSchema, VALUE));
      setSuggestions(getPropValues(stateSchema, SUGGESTION));
      setErrors(getPropValues(stateSchema, ERROR));
    }
  }, [stateSchema]); // eslint-disable-line

  const maybeErrorField = useCallback(() => Object.values(errors).some(error => error?.error || null), [errors]);

  // Set a brand new field values and errors
  // If stateSchema changes
  useEffect(() => {
    const newValues = getPropValues(state, VALUE);
    const newErrors = reduceFeedback(values, 'error', validateField);
    const newSuggestions = reduceFeedback(values, 'suggestion', validateField);
    setIsDirty(true);
    setValues(newValues);
    setErrors(newErrors);
    setSuggestions(newSuggestions);
  }, [state]); // eslint-disable-line

  useEffect(() => {
    const newErrors = reduceFeedback(values, 'error', validateField);
    const newSuggestions = reduceFeedback(values, 'suggestions', validateField);
    setErrors(newErrors);
    setErrors(newSuggestions);
  }, [validatorSchema]); // eslint-disable-line

  useEffect(() => {
    const valuesWithError = {};
    for (const field in values) {
      valuesWithError[field] = { value: values[field], error: '' };
    }
    if (isDirty) {
      setDisable(maybeErrorField() || _.isEqual(valuesWithError, defaultOptions));
    }
  }, [errors, isDirty]); // eslint-disable-line

  const setFieldValue = ({ name, value }) => {
    setValues(prevState => ({
      ...prevState,
      [name]: shouldBeArray.includes(name) ? value.split(',') : value
    }));
    setDirty(prevState => ({ ...prevState, [name]: true }));
  };

  const handleOnChange = useCallback(
    event => {
      const { name, value } = event.target;

      const processedField = validateField(name, value);

      setFieldValue({ name, value });
      if ((!processedField || typeof processedField !== 'object') && isDirty) {
        setFieldIsOk({ name });
        setFieldIsOk({ name });
      } else {
        if (processedField.suggestion) {
          setFieldSuggestion({ name, suggestion: processedField.suggestion });
        }
        if (processedField.error) {
          setFieldError({ name, error: processedField.error });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validateField]
  );

  const handleOnSubmit = useCallback(
    event => {
      event.preventDefault();
      const valuesWithoutAvailableAmount = withoutAvailableAmount(values);
      if (!maybeErrorField()) {
        return submitForm(valuesWithoutAvailableAmount);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [maybeErrorField, submitForm, values, dirty, isDirty]
  );

  return {
    fields: values,
    errors,
    suggestions,
    dirtyFields: dirty,
    handleOnChange,
    handleOnSubmit,
    disabledSubmit: disable
  };
};

export default useForm;
